import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { IconCalendarEvent, IconListDetails } from "@tabler/icons-react";
import { Moment } from "moment";
import {
  documentId,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { chunk, countBy, uniq } from "lodash";
import { useUsuario } from "../../../../providers/usuarioProvider";
import FelixCard from "../../../util/ui/felixCard";
import TabelaOperacoesInternacionais, {
  StatementLink,
} from "../../../util/tabelas/tabelaOperacoesInternacionais";
import OperacaoInternacionalDb from "../../../../db/operacaoInternacionalDb";
import UploadInternacionalDb from "../../../../db/uploadInternacionalDb";
import OperacaoInternacional from "../../../../models/operacaoInternacional";
import UploadInternacional from "../../../../models/uploadInternacional";
import { TipoOperacaoInternacional } from "../../../../models/enum";
import RegistrarEventoInternacionalDialog from "../registrarEventoInternacionalDialog";
import Functions from "../../../../callable/functions";
import { useSnackBar } from "../../../../stores/snackBarStores";

type Props = {
  mes: Moment;
};

// Rótulo por tipo (singular/plural), na ordem em que os chips de resumo
// aparecem. "Juros" é invariável — sem plural mecânico.
const ROTULOS: {
  tipo: TipoOperacaoInternacional;
  singular: string;
  plural: string;
}[] = [
  {
    tipo: TipoOperacaoInternacional.BUY,
    singular: "compra",
    plural: "compras",
  },
  { tipo: TipoOperacaoInternacional.SELL, singular: "venda", plural: "vendas" },
  {
    tipo: TipoOperacaoInternacional.DIVIDEND,
    singular: "dividendo",
    plural: "dividendos",
  },
  {
    tipo: TipoOperacaoInternacional.INTEREST,
    singular: "juros",
    plural: "juros",
  },
  {
    tipo: TipoOperacaoInternacional.CORPORATE_ACTION,
    singular: "evento",
    plural: "eventos",
  },
  {
    tipo: TipoOperacaoInternacional.WITHHOLDING_TAX,
    singular: "retenção",
    plural: "retenções",
  },
  {
    tipo: TipoOperacaoInternacional.OTHER,
    singular: "outro",
    plural: "outros",
  },
];

function tituloStatement(upload: UploadInternacional): string {
  const partes = [upload.instituicao];
  if (upload.periodo?.de && upload.periodo?.a) {
    partes.push(
      `${upload.periodo.de.format("DD/MM")} a ${upload.periodo.a.format(
        "DD/MM/YY",
      )}`,
    );
  } else {
    partes.push(upload.nomeArquivoOriginal);
  }
  return partes.filter(Boolean).join(" · ");
}

/**
 * Lista todas as linhas importadas de statements com data dentro do mês, com
 * link para o statement de origem. Responde à pergunta do contador "o que
 * entrou neste mês?" sem precisar comparar a custódia de dois meses (feedback
 * da Ana, set/2026). Mostra TODOS os tipos, inclusive OTHER, porque a
 * conferência é justamente sobre o que foi importado.
 */
export default function LancamentosDoMes({ mes }: Props) {
  const { visualizado } = useUsuario();
  const dispatchSnack = useSnackBar();
  const [operacoes, setOperacoes] = useState<OperacaoInternacional[]>([]);
  const [registrarAberto, setRegistrarAberto] = useState(false);
  const [excluindo, setExcluindo] = useState<OperacaoInternacional>();
  const [excluindoEmAndamento, setExcluindoEmAndamento] = useState(false);

  const excluirEvento = async () => {
    if (!excluindo) return;
    setExcluindoEmAndamento(true);
    try {
      await new Functions().excluirEventoInternacional({
        operacaoId: excluindo.id,
      });
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem: "Evento excluído. Recalcule o mês para aplicar.",
          severidade: "success",
        },
      });
      setExcluindo(undefined);
    } catch (error: any) {
      console.error(error);
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem: error?.message ?? "Não foi possível excluir o evento.",
          severidade: "error",
        },
      });
    } finally {
      setExcluindoEmAndamento(false);
    }
  };
  const [statements, setStatements] = useState<{
    [uploadId: string]: StatementLink;
  }>({});

  const chaveMes = mes.format("YYYY-MM");

  useEffect(() => {
    // Limpa ao trocar de cliente/mês: nada de lançamentos do anterior
    // aparecendo (com lixeira e tudo) enquanto o novo carrega.
    setOperacoes([]);
    if (!visualizado) return;
    const inicio = mes.clone().startOf("month").toDate();
    const fim = mes.clone().endOf("month").toDate();
    // Range num campo só: não exige índice composto.
    return onSnapshot(
      query(
        new OperacaoInternacionalDb().collection({
          usuarioId: visualizado,
          operacaoInternacionalId: "",
        }),
        where("dataPregao", ">=", inicio),
        where("dataPregao", "<=", fim),
        orderBy("dataPregao", "asc"),
      ),
      (snapshot) => {
        setOperacoes(
          snapshot.docs
            .map((doc) => doc.data() as OperacaoInternacional)
            // Cópias de linhas já cobertas por outro statement não contam
            .filter((op) => !op.duplicataDeUploadId),
        );
      },
      (error) => {
        // Permissão, rede ou índice: melhor lista vazia que dado velho.
        console.error(error);
        setOperacoes([]);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizado, chaveMes]);

  const uploadIds = useMemo(
    () => uniq(operacoes.map((o) => o.uploadId).filter(Boolean)).sort(),
    [operacoes],
  );

  useEffect(() => {
    if (!visualizado || uploadIds.length === 0) {
      setStatements({});
      return;
    }
    // `in` aceita até 30 ids por consulta; um mês raramente passa de 2 ou 3
    // statements, mas o chunk protege o caso de importação em massa.
    const unsubscribes = chunk(uploadIds, 30).map((ids) =>
      onSnapshot(
        query(
          new UploadInternacionalDb().collection({
            usuarioId: visualizado,
            uploadId: "",
          }),
          where(documentId(), "in", ids),
        ),
        (snapshot) => {
          setStatements((atual) => {
            const novo = { ...atual };
            snapshot.docs.forEach((doc) => {
              const upload = doc.data() as UploadInternacional;
              novo[doc.id] = {
                titulo: tituloStatement(upload),
                to: `/internacional/statement/${doc.id}`,
              };
            });
            return novo;
          });
        },
      ),
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizado, uploadIds.join(",")]);

  const resumo = useMemo(() => {
    const porTipo = countBy(operacoes, "tipoOperacao");
    return ROTULOS.filter(({ tipo }) => porTipo[tipo] > 0).map(
      ({ tipo, singular, plural }) =>
        `${porTipo[tipo]} ${porTipo[tipo] === 1 ? singular : plural}`,
    );
  }, [operacoes]);

  return (
    <FelixCard>
      <CardHeader
        title="Lançamentos"
        avatar={<IconListDetails size={16} />}
        action={
          <Box
            display="flex"
            gap={0.5}
            flexWrap="wrap"
            justifyContent="flex-end"
            alignItems="center"
          >
            {resumo.map((texto) => (
              <Chip
                key={texto}
                label={texto}
                size="small"
                sx={(theme) => ({
                  backgroundColor: theme.palette.statusChip.neutral.bg,
                  color: theme.palette.statusChip.neutral.fg,
                })}
              />
            ))}
            {/* Registro manual de desdobramento/grupamento: os leitores de
                statement ainda não extraem esses eventos. */}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<IconCalendarEvent size={16} />}
              onClick={() => setRegistrarAberto(true)}
              sx={{ ml: 0.5 }}
            >
              Registrar evento
            </Button>
          </Box>
        }
      />
      <CardContent>
        {operacoes.length > 0 ? (
          <TabelaOperacoesInternacionais
            operacoes={operacoes}
            showDate
            statements={statements}
            onExcluirManual={(operacao) => setExcluindo(operacao)}
          />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            py={2}
          >
            Nenhum lançamento importado com data neste mês. Se o statement do
            período já foi enviado, confira em Gerenciar Statements se a
            importação concluiu.
          </Typography>
        )}
      </CardContent>
      {registrarAberto && (
        <RegistrarEventoInternacionalDialog
          open={registrarAberto}
          onClose={() => setRegistrarAberto(false)}
          mes={mes}
        />
      )}
      <Dialog open={!!excluindo} onClose={() => setExcluindo(undefined)}>
        <DialogTitle color="text.primary" sx={{ typography: "h3" }}>
          Excluir evento?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {excluindo?.descricao} em{" "}
            {excluindo?.dataPregao.format("DD/MM/YYYY")}. O mês será recalculado
            sem este ajuste.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setExcluindo(undefined)}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={excluindoEmAndamento}
            onClick={excluirEvento}
          >
            Excluir
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FelixCard>
  );
}
